import { type AppDefaultInfo, type GlobalInfo } from "helpers/AppProvider.types";

import { type BTLoginTypes } from "types/enum";

/**
 * Synchronous snapshot of a few `GlobalInfo` values, for the call sites that can't reach context:
 * module-eval code, class field initializers, and plain `utilities/` functions. Populated by
 * `useGetGlobalInfoQuery`.
 *
 * Readers must tolerate an empty snapshot: it stays empty until `AppProvider` resolves, and there
 * is no other source to fall back to.
 */
interface IGlobalInfoSnapshot {
    loginType: BTLoginTypes | null;
    builderId: number | null;
    appDefaultInfo: AppDefaultInfo | null;
}

const emptySnapshot: IGlobalInfoSnapshot = {
    loginType: null,
    builderId: null,
    appDefaultInfo: null,
};

let snapshot: IGlobalInfoSnapshot = emptySnapshot;

/**
 * Called once per global info fetch, including `reloadGlobalInfo` refetches, so the snapshot
 * tracks the active session rather than the first one loaded.
 */
export function setGlobalInfoSnapshot(globalInfo: GlobalInfo): void {
    snapshot = {
        loginType: globalInfo.userInfo.loginType,
        builderId: globalInfo.builderInfo?.builderId ?? null,
        appDefaultInfo: globalInfo.appDefaultInfo,
    };
}

/** Test-only: seeds the baseline snapshot between tests. */
export function resetGlobalInfoSnapshotForTests(globalInfo: GlobalInfo): void {
    setGlobalInfoSnapshot(globalInfo);
}

/**
 * Test-only: overrides individual snapshot fields without a full `GlobalInfo`. Production code
 * should never call this — the snapshot is owned by the global info query.
 */
export function overrideGlobalInfoSnapshotForTests(overrides: Partial<IGlobalInfoSnapshot>): void {
    snapshot = { ...snapshot, ...overrides };
}

/**
 * The user's portal/login type, or null before global info has resolved.
 *
 * @deprecated Use `UserInfoContext` wherever context is reachable. These readers exist only for
 * call sites that genuinely can't, and each is expected to go away under 282446.
 */
export function getSnapshotLoginType(): BTLoginTypes | null {
    return snapshot.loginType;
}

/**
 * The builder in session, or null when global info hasn't resolved or no builder is in session.
 *
 * @deprecated Use `BuilderInfoContext` wherever context is reachable — see
 * {@link getSnapshotLoginType} for why this reader still exists.
 */
export function getSnapshotBuilderId(): number | null {
    return snapshot.builderId;
}

/**
 * App default flags from the global info payload, or null before it resolves.
 *
 * @deprecated Use `AppDefaultInfoContext` wherever context is reachable — see
 * {@link getSnapshotLoginType} for why these readers still exist.
 */
export function getSnapshotAppDefaultInfo(): AppDefaultInfo | null {
    return snapshot.appDefaultInfo;
}
